import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/PageHeader';
import api from '../api';

const COLORS = {
  forestGreen: '#0F3D2E',
  deepGreen: '#003C3C',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  softCream: '#F7F0E3',
  softBorder: '#E6D8BE',
  mutedText: '#746A5B',
  success: '#1F8F4D',
  warning: '#B85C38',
  danger: '#B54135',
};

const DEFAULT_FILTERS = {
  search: '',
  restaurantId: '',
  depositStatus: 'paid',
  reconciliationStatus: 'pending',
  dateFrom: '',
  dateTo: '',
  limit: 10,
};

const money = value => `R${Number(value || 0).toFixed(2)}`;

const dateOnly = value => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
};

const dateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const getRestaurants = response => {
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getStatusStyle = status => {
  if (status === 'reconciled') return { backgroundColor: '#E7F6ED', color: COLORS.success };
  if (status === 'refunded') return { backgroundColor: '#EEF5FF', color: '#1D5EA8' };
  if (status === 'disputed') return { backgroundColor: '#FCEAE8', color: COLORS.danger };
  return { backgroundColor: '#FFF1E8', color: COLORS.warning };
};

export default function DineInDeposits() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [deposits, setDeposits] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [meta, setMeta] = useState({ total: 0, currentPage: 1, lastPage: 1, perPage: 10 });
  const [totals, setTotals] = useState({
    count: 0,
    depositAmount: 0,
    balanceDue: 0,
    orderTotal: 0,
    restaurantPlatformFees: 0,
    payableToRestaurant: 0,
    dueFromRestaurant: 0,
    reconciledAmount: 0,
    pendingAmount: 0,
  });
  const [reconciliationCounts, setReconciliationCounts] = useState({
    pending: 0,
    reconciled: 0,
    refunded: 0,
    disputed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({});

  const loadDeposits = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getDineInDeposits({ ...filters, page });
      setDeposits(Array.isArray(response?.data) ? response.data : []);
      setMeta(response?.meta || { total: 0, currentPage: page, lastPage: 1, perPage: filters.limit });
      setTotals(response?.totals || {});
      setReconciliationCounts(
        response?.reconciliationCounts || {
          pending: 0,
          reconciled: 0,
          refunded: 0,
          disputed: 0,
        },
      );
    } catch (err) {
      setError(err.message || 'Could not load dine-in deposits.');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadDeposits();
  }, [loadDeposits]);

  useEffect(() => {
    let mounted = true;
    api.fetchAdminRestaurants()
      .then(response => {
        if (mounted) setRestaurants(getRestaurants(response));
      })
      .catch(() => {
        if (mounted) setRestaurants([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const nextDrafts = {};
    deposits.forEach(deposit => {
      nextDrafts[deposit.id] = {
        status: deposit.reconciliationStatus || 'pending',
        notes: deposit.reconciliationNotes || '',
      };
    });
    setDrafts(nextDrafts);
  }, [deposits]);

  const updateFilter = (key, value) => {
    setFilters(current => ({ ...current, [key]: value }));
    setPage(1);
  };

  const updateDraft = (id, key, value) => {
    setNotice(null);
    setDrafts(current => ({
      ...current,
      [id]: { ...(current[id] || {}), [key]: value },
    }));
  };

  const isDraftDirty = deposit => {
    const draft = drafts[deposit.id] || {};
    return (
      (draft.status || 'pending') !==
        (deposit.reconciliationStatus || 'pending') ||
      (draft.notes || '').trim() !==
        (deposit.reconciliationNotes || '').trim()
    );
  };

  const handleSave = async deposit => {
    const draft = drafts[deposit.id] || {};
    const nextStatus = draft.status || 'pending';
    const nextNotes = (draft.notes || '').trim();

    if (!isDraftDirty(deposit)) {
      setNotice({
        type: 'info',
        text: `Deposit #${deposit.orderId} has no unsaved changes.`,
      });
      return;
    }

    try {
      setSavingId(deposit.id);
      setError('');
      setNotice(null);

      const response = await api.reconcileDineInDeposit(deposit.id, {
        status: nextStatus,
        notes: nextNotes,
      });

      setNotice({
        type: 'success',
        text:
          filters.reconciliationStatus &&
          filters.reconciliationStatus !== nextStatus
            ? `Deposit #${deposit.orderId} was saved as ${nextStatus}. It no longer appears in the ${filters.reconciliationStatus} filter.`
            : response?.message ||
              `Deposit #${deposit.orderId} was saved successfully.`,
      });
      await loadDeposits();
    } catch (err) {
      const message =
        err.message || 'Could not update deposit reconciliation.';
      setError(message);
      setNotice({
        type: 'error',
        text: `Deposit #${deposit.orderId} was not saved. ${message}`,
      });
    } finally {
      setSavingId(null);
    }
  };

  const lastPage = Math.max(1, Number(meta.lastPage || 1));

  const summaryCards = useMemo(
    () => {
      const deposits = Number(totals.depositAmount || 0);
      const platformFees = Number(totals.restaurantPlatformFees || 0);

      return [
        { label: 'Total deposits', value: money(deposits) },
        { label: 'YaBatho platform fee', value: money(platformFees) },
        {
          label: 'Payable to restaurants',
          value: money(Math.max(0, deposits - platformFees)),
        },
      ];
    },
    [totals],
  );

  const followUpStatuses = [
    {
      value: 'pending',
      label: 'Pending follow-up',
      description: 'Needs reconciliation',
    },
    {
      value: 'reconciled',
      label: 'Reconciled',
      description: 'Completed by Admin',
    },
    {
      value: 'refunded',
      label: 'Refunded',
      description: 'Returned to customer',
    },
    {
      value: 'disputed',
      label: 'Disputed',
      description: 'Needs investigation',
    },
  ];

  return (
    <Layout>
      <PageHeader
        eyebrow="Dine-in finance"
        title="Dine-in Deposit Reconciliation"
        subtitle="Reconcile customer deposits, YaBatho fees, and the amount payable to restaurants."
      />

      <div style={styles.summaryGrid}>
        {summaryCards.map(card => (
          <div key={card.label} style={styles.summaryCard}>
            <span style={styles.summaryLabel}>{card.label}</span>
            <strong style={styles.summaryValue}>{card.value}</strong>
          </div>
        ))}
      </div>

      <section style={styles.followUpPanel}>
        <div style={styles.followUpHeading}>
          <div>
            <span style={styles.followUpEyebrow}>Reconciliation follow-up</span>
            <strong style={styles.followUpTitle}>
              Deposit status overview
            </strong>
          </div>
          {filters.reconciliationStatus && (
            <button
              type="button"
              style={styles.showAllButton}
              onClick={() => updateFilter('reconciliationStatus', '')}
            >
              Show all
            </button>
          )}
        </div>

        <div style={styles.followUpGrid}>
          {followUpStatuses.map(item => {
            const selected = filters.reconciliationStatus === item.value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() =>
                  updateFilter(
                    'reconciliationStatus',
                    selected ? '' : item.value,
                  )
                }
                style={{
                  ...styles.followUpCard,
                  ...(selected ? styles.followUpCardSelected : {}),
                }}
              >
                <span style={styles.followUpCount}>
                  {reconciliationCounts[item.value] || 0}
                </span>
                <span style={styles.followUpCardText}>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section style={styles.filterPanel}>
        <div style={styles.filterGrid}>
          <label style={styles.field}>
            <span>Search</span>
            <input
              type="search"
              value={filters.search}
              onChange={event => updateFilter('search', event.target.value)}
              placeholder="Order, customer, restaurant..."
              style={styles.input}
            />
          </label>

          <label style={styles.field}>
            <span>Restaurant</span>
            <select
              value={filters.restaurantId}
              onChange={event => updateFilter('restaurantId', event.target.value)}
              style={styles.input}
            >
              <option value="">All restaurants</option>
              {restaurants.map(restaurant => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.field}>
            <span>Deposit status</span>
            <select
              value={filters.depositStatus}
              onChange={event => updateFilter('depositStatus', event.target.value)}
              style={styles.input}
            >
              <option value="">All deposit statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending payment</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </label>

          <label style={styles.field}>
            <span>Reconciliation</span>
            <select
              value={filters.reconciliationStatus}
              onChange={event => updateFilter('reconciliationStatus', event.target.value)}
              style={styles.input}
            >
              <option value="">All reconciliation statuses</option>
              <option value="pending">Pending</option>
              <option value="reconciled">Reconciled</option>
              <option value="refunded">Refunded</option>
              <option value="disputed">Disputed</option>
            </select>
          </label>

          <label style={styles.field}>
            <span>From</span>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={event => updateFilter('dateFrom', event.target.value)}
              style={styles.input}
            />
          </label>

          <label style={styles.field}>
            <span>To</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={event => updateFilter('dateTo', event.target.value)}
              style={styles.input}
            />
          </label>

          <label style={styles.field}>
            <span>Rows</span>
            <select
              value={filters.limit}
              onChange={event => updateFilter('limit', Number(event.target.value))}
              style={styles.input}
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </label>

          <div style={styles.filterActions}>
            <button type="button" style={styles.refreshButton} onClick={loadDeposits}>
              Refresh
            </button>
            <button
              type="button"
              style={styles.clearButton}
              onClick={() => {
                setFilters(DEFAULT_FILTERS);
                setPage(1);
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </section>

      {notice && (
        <div
          role="status"
          style={{
            ...styles.noticeBox,
            ...(notice.type === 'success'
              ? styles.successBox
              : notice.type === 'error'
              ? styles.errorBox
              : styles.infoBox),
          }}
        >
          {notice.text}
        </div>
      )}

      {error && !notice && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.tableShell}>
        {loading ? (
          <div style={styles.emptyState}>Loading dine-in deposits...</div>
        ) : deposits.length === 0 ? (
          <div style={styles.emptyState}>No dine-in deposits found.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Reservation</th>
                <th style={styles.th}>Restaurant</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Deposit</th>
                <th style={styles.th}>Restaurant fee</th>
                <th style={styles.th}>Settlement</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Reconciliation</th>
              </tr>
            </thead>
            <tbody>
              {deposits.map(deposit => {
                const draft = drafts[deposit.id] || {};
                const dirty = isDraftDirty(deposit);
                const saving = savingId === deposit.id;
                return (
                  <tr key={deposit.id}>
                    <td style={styles.td}>
                      <strong>#{deposit.orderId}</strong>
                      <div style={styles.muted}>
                        {dateOnly(deposit.scheduledDate)} at {deposit.scheduledTime || '-'}
                      </div>
                      <div style={styles.muted}>{deposit.diningGuests || 1} guest(s)</div>
                      {deposit.reservationNotes && (
                        <div style={styles.noteText}>{deposit.reservationNotes}</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <strong>{deposit.restaurant?.name || 'Restaurant'}</strong>
                      <div style={styles.muted}>
                        {deposit.restaurant?.phone || deposit.restaurant?.ownerEmail || '-'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <strong>{deposit.customer?.name || 'Customer'}</strong>
                      <div style={styles.muted}>{deposit.customer?.email || deposit.customer?.phone || '-'}</div>
                    </td>
                    <td style={styles.td}>
                      <strong>{money(deposit.depositAmount)}</strong>
                      <div style={styles.muted}>Paid {dateTime(deposit.depositPaidAt)}</div>
                      <div style={styles.muted}>Food subtotal {money(deposit.foodSubtotal)}</div>
                      <div style={styles.muted}>Order total {money(deposit.totalPrice)}</div>
                      <div style={styles.muted}>Balance at restaurant {money(deposit.balanceDue)}</div>
                    </td>
                    <td style={styles.td}>
                      <strong>{money(deposit.restaurantPlatformFee)}</strong>
                      <div style={styles.muted}>10% of food subtotal</div>
                    </td>
                    <td style={styles.td}>
                      <strong>
                        {deposit.settlementDirection === 'collect_from_restaurant'
                          ? money(Math.abs(Number(deposit.netDepositSettlement || 0)))
                          : money(deposit.netDepositSettlement)}
                      </strong>
                      <div style={styles.muted}>
                        {deposit.settlementDirection === 'collect_from_restaurant'
                          ? 'Restaurant still owes platform'
                          : 'Pay restaurant from deposit'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...getStatusStyle(deposit.reconciliationStatus) }}>
                        {deposit.reconciliationStatus || 'pending'}
                      </span>
                      <div style={styles.muted}>Deposit {deposit.depositStatus || 'unknown'}</div>
                      <div style={styles.muted}>Order {deposit.orderStatus || '-'}</div>
                      {deposit.reconciledAt && (
                        <div style={styles.auditText}>
                          Recorded {dateTime(deposit.reconciledAt)}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.reconcileControls}>
                        <select
                          value={draft.status || 'pending'}
                          onChange={event => updateDraft(deposit.id, 'status', event.target.value)}
                          style={styles.compactSelect}
                          disabled={saving}
                        >
                          <option value="pending">Pending</option>
                          <option value="reconciled">Reconciled</option>
                          <option value="refunded">Refunded</option>
                          <option value="disputed">Disputed</option>
                        </select>
                        <input
                          value={draft.notes || ''}
                          onChange={event => updateDraft(deposit.id, 'notes', event.target.value)}
                          placeholder="Notes"
                          style={styles.notesInput}
                          maxLength={500}
                          disabled={saving}
                        />
                        <button
                          type="button"
                          disabled={saving || !dirty}
                          onClick={() => handleSave(deposit)}
                          style={{
                            ...styles.saveButton,
                            ...(!dirty || saving ? styles.disabledButton : {}),
                          }}
                        >
                          {saving ? 'Saving...' : dirty ? 'Save changes' : 'Saved'}
                        </button>
                        {dirty && !saving && (
                          <span style={styles.unsavedText}>Unsaved changes</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && (
          <div style={styles.paginationBar}>
            <span style={styles.pageText}>
              Showing {deposits.length} of {meta.total || 0} deposits
            </span>
            <div style={styles.pageActions}>
              <button
                type="button"
                style={styles.pageButton}
                disabled={page === 1}
                onClick={() => setPage(current => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span style={styles.pageText}>Page {page} of {lastPage}</span>
              <button
                type="button"
                style={styles.pageButton}
                disabled={page >= lastPage}
                onClick={() => setPage(current => Math.min(lastPage, current + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: 14,
    marginBottom: 18,
  },
  summaryCard: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 18,
    minHeight: 88,
  },
  summaryLabel: {
    display: 'block',
    color: COLORS.mutedText,
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  summaryValue: {
    display: 'block',
    color: COLORS.forestGreen,
    fontSize: 24,
    lineHeight: 1.2,
  },
  followUpPanel: {
    backgroundColor: COLORS.softCream,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 14,
    marginBottom: 16,
  },
  followUpHeading: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  followUpEyebrow: {
    display: 'block',
    color: COLORS.heritageGold,
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  followUpTitle: {
    display: 'block',
    color: COLORS.forestGreen,
    fontSize: 16,
  },
  showAllButton: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 11px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  followUpGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
  },
  followUpCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    textAlign: 'left',
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 8,
    padding: 12,
    cursor: 'pointer',
  },
  followUpCardSelected: {
    borderColor: COLORS.forestGreen,
    boxShadow: `inset 0 0 0 1px ${COLORS.forestGreen}`,
  },
  followUpCount: {
    minWidth: 44,
    height: 44,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: COLORS.forestGreen,
    color: '#ffffff',
    fontSize: 20,
    fontWeight: 900,
  },
  followUpCardText: {
    display: 'grid',
    gap: 3,
    fontSize: 13,
  },
  filterPanel: {
    backgroundColor: COLORS.softCream,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 14,
    marginBottom: 16,
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 12,
    alignItems: 'end',
  },
  field: {
    display: 'grid',
    gap: 6,
    color: COLORS.forestGreen,
    fontWeight: 900,
    fontSize: 12,
    textTransform: 'uppercase',
  },
  input: {
    minHeight: 44,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 6,
    padding: '9px 11px',
    color: COLORS.deepGreen,
    backgroundColor: COLORS.offWhite,
    fontWeight: 800,
    fontSize: 14,
    textTransform: 'none',
  },
  filterActions: {
    display: 'flex',
    gap: 10,
  },
  refreshButton: {
    border: 'none',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    borderRadius: 6,
    padding: '11px 14px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  clearButton: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '11px 14px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  errorBox: {
    backgroundColor: '#FCEAE8',
    color: COLORS.danger,
    border: `1px solid ${COLORS.danger}`,
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
    fontWeight: 800,
  },
  noticeBox: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
    fontWeight: 800,
  },
  successBox: {
    backgroundColor: '#E7F6ED',
    color: COLORS.success,
    border: `1px solid ${COLORS.success}`,
  },
  infoBox: {
    backgroundColor: '#EEF5FF',
    color: '#1D5EA8',
    border: '1px solid #8CB8E8',
  },
  tableShell: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    overflow: 'auto',
  },
  table: {
    width: '100%',
    minWidth: 1280,
    borderCollapse: 'collapse',
  },
  th: {
    backgroundColor: '#F1E7D2',
    color: COLORS.forestGreen,
    textAlign: 'left',
    padding: '14px 16px',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  td: {
    padding: '15px 16px',
    color: COLORS.forestGreen,
    borderTop: `1px solid ${COLORS.softBorder}`,
    verticalAlign: 'top',
  },
  muted: {
    color: COLORS.mutedText,
    fontSize: 12,
    marginTop: 4,
  },
  auditText: {
    color: COLORS.success,
    fontSize: 11,
    fontWeight: 800,
    marginTop: 8,
  },
  noteText: {
    color: COLORS.mutedText,
    fontSize: 12,
    marginTop: 8,
    maxWidth: 210,
    lineHeight: 1.4,
  },
  badge: {
    display: 'inline-flex',
    borderRadius: 999,
    padding: '6px 10px',
    fontWeight: 900,
    fontSize: 12,
    textTransform: 'capitalize',
  },
  reconcileControls: {
    display: 'grid',
    gridTemplateColumns: '140px minmax(180px, 1fr) 126px',
    gap: 8,
    minWidth: 470,
    alignItems: 'center',
  },
  compactSelect: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.deepGreen,
    borderRadius: 6,
    padding: '8px 10px',
    fontWeight: 800,
  },
  notesInput: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.deepGreen,
    borderRadius: 6,
    padding: '8px 10px',
    fontWeight: 700,
  },
  saveButton: {
    border: 'none',
    backgroundColor: COLORS.heritageGold,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 10px',
    cursor: 'pointer',
    fontWeight: 900,
  },
  disabledButton: {
    backgroundColor: '#E7DECC',
    color: COLORS.mutedText,
    cursor: 'not-allowed',
    opacity: 0.8,
  },
  unsavedText: {
    gridColumn: '1 / -1',
    color: COLORS.warning,
    fontSize: 11,
    fontWeight: 800,
  },
  emptyState: {
    padding: 32,
    textAlign: 'center',
    color: COLORS.mutedText,
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: 12,
    borderTop: `1px solid ${COLORS.softBorder}`,
    backgroundColor: '#FBF6EA',
  },
  pageActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  pageButton: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 11px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  pageText: {
    color: COLORS.mutedText,
    fontWeight: 800,
    fontSize: 13,
  },
};
